
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/auth';
import { useToast } from '@/hooks/use-toast';
import { sanitizeProfileUrls } from '@/utils/url-utils';

export const usePublicProfile = (identifier?: string) => {
  const { toast } = useToast();

  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['public-profile', identifier],
    queryFn: async () => {
      if (!identifier) return null;

      try {
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

        // First get the profile data without dot notation
        const query = supabase
          .from('public_profiles')
          .select('*');

        if (isUUID) {
          query.eq('id', identifier);
        } else {
          query.eq('username', identifier);
        }

        const { data: profileData, error } = await query.maybeSingle();

        if (error || !profileData) {
          console.error('Error fetching public profile:', error);
          if (error) {
            toast({
              title: "Error fetching profile",
              description: error.message,
              variant: "destructive",
            });
          }
          throw error || new Error('Profile not found');
        }
        
        // Then get follower count
        const { count: followersCount, error: followersError } = await supabase
          .from('followers')
          .select('*', { count: 'exact', head: true })
          .eq('following_id', profileData.id);
          
        if (followersError) {
          console.error('Error fetching followers count:', followersError);
        }

        // Then get following count
        const { count: followingCount, error: followingError } = await supabase
          .from('followers')
          .select('*', { count: 'exact', head: true })
          .eq('follower_id', profileData.id);
          
        if (followingError) {
          console.error('Error fetching following count:', followingError);
        }

        // Get enhanced profile with counts and validate custom_links
        const enhancedProfile = {
          ...profileData,
          followers_count: followersCount ?? 0,
          following_count: followingCount ?? 0,
          // Filter out empty or invalid custom_links entries
          custom_links: Array.isArray(profileData.custom_links) 
            ? profileData.custom_links.filter((link: any) => 
                link && 
                typeof link === 'object' && 
                link.label && 
                link.label.trim() !== '' && 
                link.url && 
                link.url.trim() !== ''
              )
            : [],
        };

        // Sanitize all URLs in the profile data
        const sanitizedData = sanitizeProfileUrls(enhancedProfile);
        console.log('Sanitized profile data:', sanitizedData);
        
        return sanitizedData as Profile;
      } catch (error) {
        console.error('Error in public profile fetch:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    retry: 2, // Retry failed requests 2 times
  });

  return {
    profile,
    isLoading,
    error,
    refetch,
  };
};
